import type { WorkspaceTab } from "@/features/workspace-router/state/workspaceStore";
import type { AndoSource } from "@ando/core/domain";
import type { SourceRailKind } from "@ando/ui";

/**
 * Single source of truth for "where does a source open in v2".
 *
 * Each ``AndoSource`` (BE wire shape) maps to:
 *
 *   - ``tab``     — TanStack Router target (``/workspace/<tab>``)
 *   - ``focus``   — value for the ``?focus=`` search param (record id)
 *   - ``kind``    — granular kind used by the ``<SourceRail>`` chip icon
 *   - ``label``   — human label for the destination tab (for cross-type
 *                   tooltip / cross-type popover line)
 *
 * Used by:
 *   - ``useSourceAutoOpen`` — picks top-1 → navigates to ``(tab, focus)``
 *   - ``SourceRailSurface`` — builds chips for ALL N sources, click =
 *     navigate via the same mapper
 *   - ``documents.tsx`` / ``conversations.tsx`` / ``calendar.tsx`` — read
 *     ``?focus`` to mount the inline viewer at the right record
 *
 * Keep this file at <100 LoC — adding a new viewer = add one switch case +
 * one tab label, every call-site picks it up automatically.
 */

export interface SourceDestination {
  /** Workspace tab the source belongs to. */
  readonly tab: WorkspaceTab;
  /** Value for the ``?focus=`` search param (record id). */
  readonly focus: string;
  /** Granular kind for the rail chip icon. */
  readonly kind: SourceRailKind;
  /** Human label of the destination tab (Italian — UI copy). */
  readonly tabLabel: string;
}

/**
 * Italian display labels — single source of truth for tab microcopy used
 * across ``destinationForSource`` (chip tooltip) and ``SourceRailSurface``
 * (current-tab comparison for the cross-type indicator). Kept local (no
 * import from ``@/features/workspace/config``) to avoid pulling icons +
 * sub-tab schemas we don't need here.
 */
export const WORKSPACE_TAB_LABEL: Record<WorkspaceTab, string> = {
  work: "Work",
  conversations: "Conversazioni",
  documents: "Documenti",
  calendar: "Calendario",
  finance: "Finance",
  sales: "Sales",
  technical: "Tecnica",
  // May 23, 2026 IA cleanup — promoted from Studio drawer to first-class
  // workspace tabs. Labels mirror the WorkspaceTabs chrome.
  canvases: "Canvas",
  dashboards: "Dashboard",
  automations: "Automazioni",
  flows: "Flows",
  shared: "Connect",
  shares: "Condivisioni",
};

/**
 * Resolve the workspace destination for a wire source. Returns ``null``
 * when the source lacks a stable id — callers should skip rendering /
 * navigating in that case (rather than rendering a broken chip).
 */
export function destinationForSource(source: AndoSource): SourceDestination | null {
  if (!source.doc_id) return null;
  switch (source.source_type) {
    case "email":
      // BE convention: ``doc_id`` is the inbox ``message_id`` for email
      // sources (the BE assembler aliases ``doc_id = message_id`` in
      // ``ando-agent`` answer assembler so the FE has one stable key).
      return {
        tab: "conversations",
        focus: source.doc_id,
        kind: "email",
        tabLabel: WORKSPACE_TAB_LABEL.conversations,
      };
    case "chat":
      // Chat threads (Teams/Slack/Google Chat) live in the inbox too.
      return {
        tab: "conversations",
        focus: source.doc_id,
        kind: "chat",
        tabLabel: WORKSPACE_TAB_LABEL.conversations,
      };
    case "web":
      // Web sources don't have a dedicated viewer yet — fall back to
      // Documenti tab + ``other`` chip kind. The viewer renders a
      // "Apri esterno" CTA. Drop this branch when the web viewer lands.
      return {
        tab: "documents",
        focus: source.doc_id,
        kind: "web",
        tabLabel: WORKSPACE_TAB_LABEL.documents,
      };
    case "document":
    case undefined:
      return {
        tab: "documents",
        focus: source.doc_id,
        kind: "document",
        tabLabel: WORKSPACE_TAB_LABEL.documents,
      };
    default: {
      // Exhaustiveness — TS surfaces a "type 'never'" diagnostic if the
      // wire enum grows a new value without a case above.
      const _exhaustive: never = source.source_type;
      void _exhaustive;
      return {
        tab: "documents",
        focus: source.doc_id,
        kind: "other",
        tabLabel: WORKSPACE_TAB_LABEL.documents,
      };
    }
  }
}
